/**
 * useMerchantAnalytics - React Query hook for Admin Merchant Analytics
 * Fetches stats and charts from GET /admin/merchants/:merchantId/analytics/*
 */

import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type { RevenueTrendData } from '../services/dashboardService';

export type MerchantAnalyticsRange = '7d' | '30d' | 'lifetime';

export interface MerchantAnalyticsStats {
  range: MerchantAnalyticsRange;
  total_orders: number;
  period_revenue: number;
  lifetime_revenue: number;
  lifetime_orders: number;
  avg_rating: number;
  avg_order_value: number;
  growth_percent: number | null;
}

const QUERY_KEY_STATS = (merchantId: string, range: string) =>
  ['admin', 'merchants', merchantId, 'analytics', 'stats', range] as const;
const QUERY_KEY_CHARTS = (merchantId: string, range: string) =>
  ['admin', 'merchants', merchantId, 'analytics', 'charts', range] as const;

async function fetchMerchantStats(
  merchantId: string,
  range: MerchantAnalyticsRange
): Promise<MerchantAnalyticsStats> {
  const res = await api.get<{
    success?: boolean;
    data?: MerchantAnalyticsStats;
  }>(`/admin/merchants/${merchantId}/analytics/stats`, {
    params: { range },
  });
  const data = res.data?.data;
  if (!data) {
    return {
      range,
      total_orders: 0,
      period_revenue: 0,
      lifetime_revenue: 0,
      lifetime_orders: 0,
      avg_rating: 0,
      avg_order_value: 0,
      growth_percent: null,
    };
  }
  return {
    range: data.range ?? range,
    total_orders: data.total_orders ?? 0,
    period_revenue: data.period_revenue ?? data.lifetime_revenue ?? 0,
    lifetime_revenue: data.lifetime_revenue ?? 0,
    lifetime_orders: data.lifetime_orders ?? data.total_orders ?? 0,
    avg_rating: data.avg_rating ?? 0,
    avg_order_value: data.avg_order_value ?? 0,
    growth_percent: data.growth_percent ?? null,
  };
}

async function fetchMerchantCharts(
  merchantId: string,
  range: string
): Promise<RevenueTrendData[]> {
  const res = await api.get<{
    success?: boolean;
    data?: {
      revenue_trend?: Array<{ date: string; revenue: number; orders: number }>;
    };
  }>(`/admin/merchants/${merchantId}/analytics/charts`, { params: { range } });
  const list = res.data?.data?.revenue_trend ?? [];
  return list.map(row => ({
    date: row.date,
    revenue: row.revenue ?? 0,
    orders: row.orders ?? 0,
  }));
}

export function useMerchantAnalytics(
  merchantId: string | undefined,
  options?: { range?: MerchantAnalyticsRange }
) {
  const range = options?.range ?? '7d';

  const statsQuery = useQuery({
    queryKey: QUERY_KEY_STATS(merchantId ?? '', range),
    queryFn: () => fetchMerchantStats(merchantId!, range),
    enabled: !!merchantId,
  });

  const chartsQuery = useQuery({
    queryKey: QUERY_KEY_CHARTS(merchantId ?? '', range),
    queryFn: () => fetchMerchantCharts(merchantId!, range),
    enabled: !!merchantId && range !== 'lifetime',
  });

  return {
    stats: statsQuery.data,
    statsLoading: statsQuery.isLoading,
    statsError: statsQuery.error,
    charts: chartsQuery.data ?? [],
    chartsLoading: chartsQuery.isLoading,
    chartsError: chartsQuery.error,
    refetchStats: statsQuery.refetch,
    refetchCharts: chartsQuery.refetch,
  };
}
